import React from 'react';
import { gameplayPresentation } from '@/config/presentationSlots';

/**
 * Phase 5.0B FINAL — Pitch background (strict top-down orthographic).
 *
 * Layered stack, back → front:
 *   1. pitch_grass_default.webp        (raster grass, top-down)
 *   2. pitch_markings.svg              (white lines, orthographic)
 *   3. Two premium sideline ad panels  (leftSidelineAdAsset / rightSidelineAdAsset).
 *      Vertically centred, occupy the full 14%–86% rail window
 *      (=72% of pitch height, top/bottom cutoffs symmetric around
 *      the pitch vertical centre). Keeps intrinsic aspect ratio.
 *      Never mirrored, rotated or flipped.
 *
 * NOTE: the old `stadium_shell_default.png` layer used to paint the
 * decorative black/stadium side rails behind the banner. Now that the
 * premium banners are FINAL production art and carry their own edge
 * treatment, that layer is removed entirely — nothing renders behind
 * the banner on the sideline strips. The asset remains registered
 * in `presentationSlots` for future themes but is no longer used.
 *
 * Zero perspective. Zero cover-crop. Board geometry unaffected.
 */
const A = gameplayPresentation.assets;

export default function PitchBackground() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* LAYER 1 — grass (top-down orthographic) */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${A.pitchGrass})`,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      {/* LAYER 1b — restrained grass darkening / contrast tune. Never
          touches cards or HUD (both rendered above this layer). */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0.12) 100%)',
        }}
      />
      {/* LAYER 2 — pitch markings (SVG, non-scaling stroke) */}
      <img
        aria-hidden
        src={A.pitchMarkings}
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: 'fill', pointerEvents: 'none', opacity: 0.75 }}
        alt=""
      />
      {/* LAYER 3 — premium sideline advertisement panels.
          Fill the current 14%–86% rail window (height: 72%, centred
          via top: 50% + translateY(-50%)). Width follows the asset's
          own aspect ratio. Never mirrored / rotated / flipped. */}
      <img
        aria-hidden
        src={A.leftSidelineAdAsset}
        alt=""
        className="kk-sideline-ad kk-sideline-ad--left"
        data-testid="sideline-ad-left"
      />
      <img
        aria-hidden
        src={A.rightSidelineAdAsset}
        alt=""
        className="kk-sideline-ad kk-sideline-ad--right"
        data-testid="sideline-ad-right"
      />
    </div>
  );
}
